import {
  ChallengeCompletionStatus,
  ChallengeParticipantStatus,
  ChallengeTaskType,
  ChallengeVerification,
} from "@fitness-app/data-models/entities/Challenge";

import { supabase } from "~/store/initializeStore";
import {
  forgetSimulatorWrite,
  getSimulatorWrite,
  logSimulatorWrite,
  SIMULATOR_TABLE,
  wasCreatedBySimulator,
} from "./simulatorWriteLog";

export type SimulatorChallengeTask = {
  id: string;
  title: string;
  type: ChallengeTaskType;
  verification: ChallengeVerification;
  points: number;
  target: number | null;
  /** Odhaczenie tego zadania na wybrany dzień, jeśli istnieje. */
  completionId: string | null;
  /** Status tego odhaczenia — potrzebny, żeby czyszczenie przywróciło stan sprzed symulatora. */
  completionStatus: ChallengeCompletionStatus | null;
};

export type SimulatorChallenge = {
  challengeId: string;
  participantId: string;
  name: string;
  startDate: string;
  endDate: string;
  points: number | null;
  rank: number | null;
  tasks: SimulatorChallengeTask[];
};

/**
 * Zadania typu `habit` punktują z `habit_completion`, `planActivity` z `client_activity`,
 * a `exerciseRecording` z `exercise_recording` — żadnego z nich nie odhacza się wpisem do
 * `challenge_task_completion`. Bezpośrednio zgłaszalne są tylko te dwa.
 */
export const DIRECTLY_COMPLETABLE_TASK_TYPES: ChallengeTaskType[] = [
  ChallengeTaskType.ExtraTask,
  ChallengeTaskType.NumericGoal,
];

export const taskScoringSource = (type: ChallengeTaskType): string => {
  switch (type) {
    case ChallengeTaskType.Habit:
      return "zakładka Nawyki";
    case ChallengeTaskType.PlanActivity:
      return "aktywność (trening / dieta / pomiary)";
    case ChallengeTaskType.ExerciseRecording:
      return "nagranie ćwiczenia";
    default:
      return "";
  }
};

/** Wyzwania, w których podopieczna bierze udział, z zadaniami i odhaczeniami na wskazany dzień. */
export const listTraineeChallenges = async (traineeId: string, date: string): Promise<SimulatorChallenge[]> => {
  const { data: participants, error } = await supabase
    .from("challenge_participant")
    .select("id, challengeId, status, challenge:challengeId(id, name, startDate, endDate)")
    .eq("traineeId", traineeId)
    .in("status", [ChallengeParticipantStatus.Active, ChallengeParticipantStatus.Completed]);

  if (error) {
    throw new Error(error.message);
  }

  const rows = (participants ?? []).filter((row) => row.challenge);

  if (rows.length === 0) {
    return [];
  }

  const challengeIds = rows.map((row) => row.challengeId);

  const [tasksResult, completionsResult, snapshotsResult] = await Promise.all([
    supabase
      .from("challenge_task")
      .select("id, challengeId, title, type, verification, points, target, sortOrder")
      .in("challengeId", challengeIds)
      .order("sortOrder", { ascending: true }),
    supabase
      .from("challenge_task_completion")
      .select("id, taskId, participantId, status")
      .eq("traineeId", traineeId)
      .eq("date", date)
      .in("challengeId", challengeIds),
    supabase
      .from("challenge_score_snapshot")
      .select("challengeId, points, rank")
      .eq("traineeId", traineeId)
      .in("challengeId", challengeIds),
  ]);

  for (const result of [tasksResult, completionsResult, snapshotsResult]) {
    if (result.error) {
      throw new Error(result.error.message);
    }
  }

  const completionByTask = new Map(
    (completionsResult.data ?? []).map((completion) => [
      `${completion.participantId}:${completion.taskId}`,
      completion,
    ]),
  );
  const snapshotByChallenge = new Map((snapshotsResult.data ?? []).map((snapshot) => [snapshot.challengeId, snapshot]));

  return rows.map((row) => {
    const snapshot = snapshotByChallenge.get(row.challengeId);
    return {
      challengeId: row.challengeId,
      participantId: row.id,
      name: row.challenge!.name,
      startDate: row.challenge!.startDate,
      endDate: row.challenge!.endDate,
      points: snapshot?.points ?? null,
      rank: snapshot?.rank ?? null,
      tasks: (tasksResult.data ?? [])
        .filter((task) => task.challengeId === row.challengeId)
        .map((task) => ({
          id: task.id,
          title: task.title,
          type: task.type as ChallengeTaskType,
          verification: task.verification as ChallengeVerification,
          points: task.points,
          target: task.target,
          completionId: completionByTask.get(`${row.id}:${task.id}`)?.id ?? null,
          completionStatus:
            (completionByTask.get(`${row.id}:${task.id}`)?.status as ChallengeCompletionStatus | undefined) ?? null,
        })),
    } satisfies SimulatorChallenge;
  });
};

type ToggleTaskParams = {
  challengeId: string;
  participantId: string;
  task: SimulatorChallengeTask;
  traineeId: string;
  date: string;
  createdBy: string;
  completed: boolean;
  mode: "single" | "bulk";
};

/**
 * Odhacza (lub cofa) zadanie wyzwania na wskazany dzień — ten sam upsert, którego używa
 * klientka w client-web, tyle że z dowolną datą. Trigger na `challenge_task_completion`
 * przelicza punkty i ranking.
 */
export const toggleChallengeTaskCompletion = async ({
  challengeId,
  participantId,
  task,
  traineeId,
  date,
  createdBy,
  completed,
  mode,
}: ToggleTaskParams): Promise<void> => {
  if (!completed) {
    if (task.completionId) {
      const entry = await getSimulatorWrite(SIMULATOR_TABLE.challengeTaskCompletion, task.completionId);

      // Zgłoszenie zrobione przez klientkę kasujemy bezpowrotnie — po DELETE nie ma z czego
      // odtworzyć wiersza, więc symulator go nie rusza.
      if (entry && !wasCreatedBySimulator(entry)) {
        throw new Error(
          "To zadanie zgłosiła klientka — symulator go nie cofa. Użyj „wyczyść dane symulatora”, żeby wrócić do jej stanu.",
        );
      }
    }

    const { error } = await supabase
      .from("challenge_task_completion")
      .delete()
      .match({ participantId, taskId: task.id, date });

    if (error) {
      throw new Error(error.message);
    }

    if (task.completionId) {
      await forgetSimulatorWrite(SIMULATOR_TABLE.challengeTaskCompletion, task.completionId);
    }
    return;
  }

  // Zadania wymagające akceptacji trenera lądują jako `pending` i punktują dopiero po
  // zatwierdzeniu w kolejce — symulator celowo tego nie obchodzi, bo to osobna ścieżka do testu.
  const status =
    task.verification === ChallengeVerification.TrainerApproval
      ? ChallengeCompletionStatus.Pending
      : ChallengeCompletionStatus.Auto;

  const { data, error } = await supabase
    .from("challenge_task_completion")
    .upsert(
      {
        challengeId,
        participantId,
        taskId: task.id,
        traineeId,
        date,
        status,
        value: task.type === ChallengeTaskType.NumericGoal ? task.target : null,
      },
      { onConflict: "taskId,participantId,date" },
    )
    .select("id")
    .single();

  if (error) {
    throw new Error(error.message);
  }

  await logSimulatorWrite({
    traineeId,
    createdBy,
    tableName: SIMULATOR_TABLE.challengeTaskCompletion,
    rowId: data.id,
    eventDate: date,
    mode,
    // Odhaczenie zadania to wiersz utworzony przez symulator, chyba że klientka zgłosiła
    // je wcześniej sama — wtedy zostaje tylko przestawienie statusu.
    op: task.completionId ? "update" : "insert",
    before: task.completionId ? { status: task.completionStatus } : undefined,
    label: task.title,
  });
};
